import { uniq, uniqWith } from 'es-toolkit';
import React from 'react';
import { Box, Paper, SxProps, Theme, Typography } from '@mui/material';

import { INDETERMINATE, isIndeterminate } from '@paragon/client-tools';
import { Member } from '@paragon/contract-library';
import { hasTrussDesignPermission } from '@paragon/login-tools';
import {
  applyLumberPatch,
  lumberCanBeUsedForComponentThickness,
  lumbersAreEqual,
  LumberSelects,
} from '@paragon/material-lists';

import { editLumbers } from '../../../../../store/actions/editMemberActions';
import { getActiveComponentDesignGuid } from '../../../../../store/selectors/getActiveComponentDesignSelector';
import { getUniqueLumbersInLumberListForActiveComponentDesign } from '../../../../../store/selectors/getUniqueLumbersInLumberList';
import { propertiesShouldBeViewOnly } from '../../../../../store/selectors/viewOnlyModeSelectors';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { LumberDesignValues } from '../LumberDesignValues';

type Props = ({ readonly member: Member } | { readonly members: Member[] }) & {
  readonly sx?: SxProps<Theme>;
  readonly showAdvanced?: boolean;
};

export function LumberPropertySubPanel(props: Props) {
  const { sx, showAdvanced } = props;

  const dispatch = useAppDispatch();

  const members = 'members' in props ? props.members : [props.member];
  const componentDesignGuid = useAppSelector(getActiveComponentDesignGuid)!;

  const inventoryLumbers = useAppSelector(getUniqueLumbersInLumberListForActiveComponentDesign);
  const isTrussDesigner = useAppSelector(hasTrussDesignPermission);
  const isViewOnly = useAppSelector(propertiesShouldBeViewOnly);

  const thicknesses = uniq(members.map((member) => member.thickness));
  const commonThickness = thicknesses.length === 1 ? thicknesses[0] : INDETERMINATE;

  if (isIndeterminate(commonThickness)) {
    return (
      <Paper elevation={0} variant="outlined" sx={[{ marginTop: 1 }, ...(Array.isArray(sx) ? sx : [sx])]}>
        <Typography variant="body1" color="textSecondary" sx={{ margin: 2 }}>
          Members with thickness are selected; editing lumber is not possible.
        </Typography>
      </Paper>
    );
  }

  const lumbers = inventoryLumbers.filter((lumber) => lumberCanBeUsedForComponentThickness(lumber, commonThickness));
  const memberLumbers = uniqWith(
    members.map((member) => member.lumber),
    lumbersAreEqual,
  );

  return (
    <Box sx={sx}>
      <LumberSelects
        strict
        disabled={isViewOnly}
        showAdvanced={showAdvanced}
        value={memberLumbers}
        lumbers={lumbers}
        onChange={(patch) => {
          dispatch(
            editLumbers(
              componentDesignGuid,
              members.map((member) => [member, applyLumberPatch(member.lumber, patch, lumbers)]),
            ),
          );
        }}
      />

      {isTrussDesigner && showAdvanced && members.length === 1 && <LumberDesignValues lumber={members[0].lumber} />}
    </Box>
  );
}
